/**
 * Master Card Component for Verification Page
 * Displays master info with documents and action buttons
 * Requirements: 16.1
 */

import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  User, 
  Phone, 
  MapPin, 
  Wrench, 
  FileText, 
  CheckCircle, 
  XCircle,
  Eye,
  Loader2,
  Calendar,
} from 'lucide-react';
import type { Master, Document } from '@/lib/api';

interface MasterCardProps {
  master: Master;
  onApprove: (masterId: string) => void;
  onReject: (masterId: string, reason: string) => void;
  isApproving?: boolean;
  isRejecting?: boolean;
}

/**
 * Format date to readable string
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get document type label
 */
function getDocumentTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    PASSPORT: 'Паспорт',
    CERTIFICATE: 'Сертификат',
    LICENSE: 'Лицензия',
    INN: 'ИНН',
    SELFEMPLOYED: 'Самозанятость',
    OTHER: 'Другое',
  };
  return labels[type] || type;
}

/**
 * Get master type label
 */
function getMasterTypeLabel(type: string | null): string {
  if (!type) return 'Не указан';
  const labels: Record<string, string> = {
    SELF_EMPLOYED: 'Самозанятый',
    ORGANIZATION: 'Организация',
  };
  return labels[type] || type;
}

export function MasterCard({ 
  master, 
  onApprove, 
  onReject,
  isApproving = false,
  isRejecting = false,
}: MasterCardProps) {
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  const handleReject = () => {
    if (rejectReason.trim().length < 10) {
      return;
    }
    onReject(master.id, rejectReason.trim());
    setShowRejectDialog(false);
    setRejectReason('');
  };

  const handleApprove = () => {
    onApprove(master.id);
  };

  const isLoading = isApproving || isRejecting;

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">{master.name || 'Без имени'}</CardTitle>
                <CardDescription className="flex items-center gap-1 text-xs">
                  <Calendar className="h-3 w-3" />
                  {formatDate(master.createdAt)}
                </CardDescription>
              </div>
            </div>
            <Badge variant="secondary">
              {getMasterTypeLabel(master.masterType)}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-3 pb-3">
          {/* Contact Info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span>{master.phone || 'Телефон не указан'}</span>
          </div>

          {/* Districts */}
          {master.districts.length > 0 && (
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="flex flex-wrap gap-1">
                {master.districts.slice(0, 3).map((district) => (
                  <Badge key={district} variant="outline" className="text-xs">
                    {district}
                  </Badge>
                ))}
                {master.districts.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{master.districts.length - 3}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Brands */}
          {master.brands.length > 0 && (
            <div className="flex items-start gap-2 text-sm">
              <Wrench className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="flex flex-wrap gap-1">
                {master.brands.slice(0, 4).map((brand) => (
                  <Badge key={brand} variant="outline" className="text-xs">
                    {brand}
                  </Badge>
                ))}
                {master.brands.length > 4 && (
                  <Badge variant="outline" className="text-xs">
                    +{master.brands.length - 4}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="flex items-start gap-2 text-sm">
            <FileText className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="flex flex-wrap gap-1">
              {master.documents.length > 0 ? (
                <>
                  {master.documents.map((doc) => (
                    <Badge 
                      key={doc.id} 
                      variant={doc.verified ? 'success' : 'secondary'}
                      className="text-xs cursor-pointer"
                      onClick={() => {
                        setSelectedDocument(doc);
                        setShowDocumentsDialog(true);
                      }}
                    >
                      {getDocumentTypeLabel(doc.type)}
                      {doc.verified && <CheckCircle className="ml-1 h-3 w-3" />}
                    </Badge>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-2 text-xs"
                    onClick={() => setShowDocumentsDialog(true)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Просмотр
                  </Button>
                </>
              ) : (
                <span className="text-muted-foreground">Документы не загружены</span>
              )}
            </div>
          </div>
        </CardContent>

        <CardFooter className="gap-2 border-t pt-3">
          <Button
            variant="success"
            size="sm"
            className="flex-1"
            onClick={handleApprove}
            disabled={isLoading}
          >
            {isApproving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-1" />
                Одобрить
              </>
            )}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="flex-1"
            onClick={() => setShowRejectDialog(true)}
            disabled={isLoading}
          >
            {isRejecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <XCircle className="h-4 w-4 mr-1" />
                Отклонить
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Отклонить заявку</DialogTitle>
            <DialogDescription>
              Укажите причину отклонения заявки мастера {master.name}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Причина отклонения (минимум 10 символов)..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRejectDialog(false)}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectReason.trim().length < 10 || isRejecting}
            >
              {isRejecting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Отклонить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={showDocumentsDialog} onOpenChange={setShowDocumentsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Документы мастера</DialogTitle>
            <DialogDescription>
              {master.name} — {master.documents.length} документ(ов)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {master.documents.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Документы не загружены
              </p>
            ) : (
              master.documents.map((doc) => (
                <div
                  key={doc.id}
                  className={`p-3 rounded-lg border ${
                    selectedDocument?.id === doc.id 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border'
                  }`}
                  onClick={() => setSelectedDocument(doc)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">
                          {getDocumentTypeLabel(doc.type)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Загружен: {formatDate(doc.createdAt)}
                        </p>
                      </div>
                    </div>
                    <Badge variant={doc.verified ? 'success' : 'secondary'}>
                      {doc.verified ? 'Проверен' : 'На проверке'}
                    </Badge>
                  </div>
                  {/* Document preview placeholder */}
                  <div className="mt-3 aspect-[4/3] bg-muted rounded-md flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">Превью документа</p>
                      <p className="text-xs opacity-70">ID: {doc.id.slice(0, 8)}...</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDocumentsDialog(false)}
            >
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
